import { FC, useCallback, useMemo } from "react"
import { DateTime } from "luxon"
import Table from "../../table"
import ListSkeleton from "../../skeletons/normal-list"
import { useFetchSubscriptionActivityStatsQuery } from "../../../../redux/queries"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  BBColumnDef,
  EngagementsTab,
  SubscriptionActivityStat
} from "../../../../types"
import DateFilter, { DateRangePreset } from "../../activity/date-filter"

type EngagementsActivityTableProps = {
  orgId: string | undefined
  brandId: string | undefined
  loid: string | undefined
  columns: BBColumnDef<SubscriptionActivityStat>[]
}

const EngagementsActivityTable: FC<EngagementsActivityTableProps> = ({
  orgId,
  brandId,
  loid,
  columns
}) => {
  const dispatch = useAppDispatch()
  const tabKey = EngagementsTab.activity
  const dateFilter = useAppSelector(state => selectTabRange(state, tabKey))

  // For activity stats, we just need the range value (API uses event_range parameter)
  const rangeInDays = useMemo(() => {
    switch (dateFilter.range) {
      case "today":
        return 1
      case "last_7":
        return 7
      case "last_30":
        return 30
      case "last_90":
        return 90
      case "custom":
        if (dateFilter.customStartDate && dateFilter.customEndDate) {
          const start = DateTime.fromJSDate(dateFilter.customStartDate)
          const end = DateTime.fromJSDate(dateFilter.customEndDate)
          return Math.ceil(end.diff(start, "days").days)
        }
        return 30
      case "all":
      default:
        return 1000
    }
  }, [dateFilter])

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, customStartDate?: Date, customEndDate?: Date) => {
      dispatch(
        updateRange({
          tabKey,
          range,
          customStartDate: customStartDate || null,
          customEndDate: customEndDate || null
        })
      )
    },
    [dispatch, tabKey]
  )

  const handleClearDateFilter = useCallback(() => {
    dispatch(
      updateRange({
        tabKey,
        range: "all",
        customStartDate: null,
        customEndDate: null
      })
    )
  }, [dispatch, tabKey])

  // Fetch subscription activity stats
  const { data: subscriptionActivityStats, isFetching } =
    useFetchSubscriptionActivityStatsQuery(
      {
        event_range: rangeInDays,
        sort_by: "num_total_dashboard_views",
        org_id: orgId,
        brand_id: brandId,
        loid
      },
      { skip: !orgId && !brandId && !loid }
    )

  return (
    <Table
      isLoading={isFetching}
      data={subscriptionActivityStats ?? []}
      columns={columns}
      viewType="table"
      searchPlaceholder="Search in activity…"
      extraHeaderElement={
        <DateFilter
          selectedRange={dateFilter.range}
          customStartDate={dateFilter.customStartDate}
          customEndDate={dateFilter.customEndDate}
          onRangeChange={handleDateRangeChange}
          onClearFilter={handleClearDateFilter}
        />
      }
      extraHeaderElementClassName="w-full md:w-auto md:ml-auto"
      noSorter
      defaultSortBy="total-dashboard-views"
      defaultSortDir="desc"
      tableClassName="md:table-fixed"
      loaderSkeleton={<ListSkeleton noOfRows={10} noOfColumns={4} />}
      containColumnsSorter
      removeMobilePagination
    />
  )
}

export default EngagementsActivityTable
